import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import {
    CONTENT_DEFAULT_BRANCH,
    type ContentBranchHistoryItem,
    getRevisionCreationTime,
} from '@gitbook/hive-core';
import { useHiveBranchHistory } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    LoadingIndicator,
    type SectionRenderItemInfo,
    TimelineList,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { HiveBranchHistoryItem } from './HiveBranchHistoryItem';

/**
 * Timeline representing the history of a branch.
 */
export function HiveBranchHistory(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    initialRevisionId: string;
    activeRevisionId?: string;
    filter?: {
        git?: boolean;
    };
    blankslate?: React.ReactNode;
    readOnly?: boolean;
}) {
    const {
        space,
        changeRequest,
        initialRevisionId,
        activeRevisionId,
        filter = { git: false },
        readOnly = false,
        blankslate,
    } = props;

    const [loading, wrapLoading] = useLoadingState();
    const { history, loadMore } = useHiveBranchHistory({
        contentId: space.id,
        branchId: changeRequest
            ? getChangeRequestBranchId(changeRequest.id)
            : CONTENT_DEFAULT_BRANCH,
        initialPage: initialRevisionId,
        limit: 10,
        git: filter.git,
    });

    const getRevisionTime = React.useCallback((revisionChanges: ContentBranchHistoryItem) => {
        const { revision } = revisionChanges;
        return getRevisionCreationTime(revision);
    }, []);

    const renderItem = React.useCallback(
        (itemProps: SectionRenderItemInfo<ContentBranchHistoryItem>) => {
            const revisionChanges = itemProps.item;
            return (
                <HiveBranchHistoryItem
                    space={space}
                    changeRequest={changeRequest}
                    revisionChanges={revisionChanges}
                    active={activeRevisionId === revisionChanges.revision.id}
                    readOnly={readOnly}
                />
            );
        },
        [space, changeRequest, activeRevisionId, readOnly]
    );

    if (history.length === 0) {
        return <>{blankslate}</>;
    }

    const onPressLoadMore = async () => {
        await wrapLoading(async () => {
            if (loadMore) {
                await loadMore();
            }
        });
    };

    return (
        <div className="flex flex-col p-1">
            <TimelineList
                data={history}
                getItemTime={getRevisionTime}
                getItemId={(item) => item.revision.id}
                renderItem={renderItem}
                dependencies={[activeRevisionId]}
            />
            {loadMore ? (
                <div className="flex flex-col pt-4">
                    <Button
                        kind="secondary"
                        label="Load more"
                        trailingIcon={loading ? LoadingIndicator : Icon.ArrowDown}
                        onPress={onPressLoadMore}
                        disabled={loading}
                    >
                        Load more
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
